"use client"

import { useCallback, useContext, useEffect, useMemo, useRef } from "react"
import type { ReactNode } from "react"

import { QueryClientContext, type QueryClient } from "@tanstack/react-query"

import type { BrowserSessionStatus } from "@/platform/api/session-contract"
import { AppModeStateContext, type AppModeStateApi } from "@/platform/app-mode/state-context"
import { invalidationDomainsFor, localInvalidationKeysFor, type StateInvalidationTrigger } from "@/platform/app-mode/state-invalidation"
import { useScreenVisible } from "@/platform/app-mode/use-screen-visible"
import { claimBridgeEvent } from "@/platform/bridge/document-events"
import type { CheckoutCompletedData } from "@/platform/bridge/types"
import { useNativeBridge } from "@/platform/bridge/use-native-bridge"
import { invalidateDomain } from "@/platform/session/private-keys"
import { revocationOf } from "@/platform/session/revocation"
import { sessionKeys } from "@/platform/session/session-keys"
import { viewerOf } from "@/platform/session/viewer"
import { refreshLastCheckoutProductRef, useLastCheckoutProductRef } from "@/platform/shopper/carts.client"

const canReadPrivateData = (queryClient: QueryClient): boolean => {
  const status = queryClient.getQueryData<BrowserSessionStatus>(sessionKeys.probe)
  return status !== undefined && viewerOf(status) !== null && revocationOf(queryClient) === "none"
}

type Invalidate = (trigger: StateInvalidationTrigger) => void

type ReportCheckoutCompleted = (completion: CheckoutCompletedData) => void

export const AppModeState = ({ children }: { children?: ReactNode }) => {
  const bridge = useNativeBridge()
  const queryClient = useContext(QueryClientContext)
  const cart = useLastCheckoutProductRef()
  const previousCart = useRef(cart)
  const visible = useScreenVisible()
  const previousVisibility = useRef({ visible: false, seen: false })

  const invalidate = useCallback<Invalidate>(
    (trigger) => {
      if (queryClient) void invalidateDomain(queryClient, ...localInvalidationKeysFor(trigger))
      if (!bridge.available) return
      bridge.notify("state.invalidate", crypto.randomUUID(), { keys: [...invalidationDomainsFor(trigger)] })
    },
    [bridge, queryClient]
  )

  const postCheckoutCompleted = useCallback<ReportCheckoutCompleted>(
    (completion) => {
      if (!bridge.available) return

      const id = claimBridgeEvent(`checkout.completed:${completion.loanUuid}`)
      if (id === null) return
      bridge.notify("checkout.completed", id, completion)
    },
    [bridge]
  )

  useEffect(() => {
    if (previousCart.current?.variantId !== cart?.variantId) invalidate("cart")
    previousCart.current = cart
  }, [cart, invalidate])

  useEffect(() => {
    if (!bridge.available || !queryClient) {
      previousVisibility.current = { visible: false, seen: false }
      return
    }
    const previous = previousVisibility.current
    previousVisibility.current = { visible, seen: previous.seen || visible }
    if (!visible || previous.visible || !previous.seen || !canReadPrivateData(queryClient)) return
    previousCart.current = refreshLastCheckoutProductRef()
    void invalidateDomain(queryClient, ...localInvalidationKeysFor("wishlist"), ...localInvalidationKeysFor("cart"))
  }, [bridge.available, queryClient, visible])

  const value = useMemo<AppModeStateApi>(() => ({ invalidate, postCheckoutCompleted }), [invalidate, postCheckoutCompleted])

  return <AppModeStateContext.Provider value={value}>{children}</AppModeStateContext.Provider>
}
